import React, { useEffect, useMemo, useState } from 'react';
import { fetchAdminAccountsOverview } from '../../services/vectorDashboardApi';
import { BROKER_BRAND, resolveBrokerKey } from './brokerBranding';
import { sparklinePositive } from './clientReportingFormat';

function formatPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return `${Number(value).toFixed(2)}%`;
}

function formatSignedPct(value) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const num = Number(value);
  return `${num >= 0 ? '+' : ''}${num.toFixed(2)}%`;
}

function initialOf(text) {
  return String(text || '?').trim().slice(0, 1).toUpperCase() || '?';
}

/** User profile picture; falls back to an initial when there's no pfp. */
function UserBadge({ account }) {
  if (account.pictureUrl) {
    return (
      <span className="vd-broker-logo has-image sm" aria-hidden="true">
        <img src={account.pictureUrl} alt="" referrerPolicy="no-referrer" />
      </span>
    );
  }
  return (
    <span className="vd-broker-logo sm" aria-hidden="true">
      {initialOf(account.userName)}
    </span>
  );
}

/** Brokerage logo for the account column. */
function BrokerBadge({ account }) {
  const key = resolveBrokerKey(account.name, account.broker);
  const brand = key ? BROKER_BRAND[key] : null;
  if (brand?.logoSrc) {
    return (
      <span className={`vd-broker-logo ${brand.tone} has-image sm`} aria-hidden="true">
        <img src={brand.logoSrc} alt="" />
      </span>
    );
  }
  return (
    <span className="vd-broker-logo sm" aria-hidden="true">
      {brand?.shortName || (account.broker || '?').slice(0, 2).toUpperCase()}
    </span>
  );
}

/** Inline SVG sparkline; green when the series trends up, red when down. */
function Sparkline({ values, positive }) {
  if (!values || values.length < 2) {
    return <span className="vd-overview-sparkline-empty">—</span>;
  }

  const width = 120;
  const height = 34;
  const pad = 2;
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const points = values.map((v, i) => {
    const x = pad + (i / (values.length - 1)) * (width - pad * 2);
    const y = height - pad - ((v - min) / range) * (height - pad * 2);
    return [x, y];
  });

  const line = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  const area = `${line} L${points[points.length - 1][0].toFixed(1)},${height} L${points[0][0].toFixed(1)},${height} Z`;
  const stroke = positive ? '#4ade80' : '#f87171';
  const fill = positive ? 'rgba(74, 222, 128, 0.14)' : 'rgba(248, 113, 113, 0.14)';

  return (
    <svg
      className="vd-overview-sparkline"
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      aria-hidden="true"
    >
      <path d={area} fill={fill} stroke="none" />
      <path d={line} fill="none" stroke={stroke} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

const OVERVIEW_RANGES = ['1M', '3M', '1Y', 'All'];

export default function AdminAccountsOverview({
  activeAccountId = null,
  onSelectAccount,
}) {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('All');

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await fetchAdminAccountsOverview(dateRange);
        if (!cancelled) {
          setAccounts(res.accounts || []);
        }
      } catch (e) {
        if (!cancelled) {
          console.error(e);
          setError(e.message);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
  }, [dateRange]);

  const emptyState = useMemo(() => {
    if (loading) return 'Loading users…';
    if (error) return null;
    if (!accounts.length) return 'No connected accounts found.';
    return null;
  }, [loading, error, accounts.length]);

  return (
    <section className="vd-client-chart-section">
      <div className="vd-panel vd-client-report-panel vd-overview-panel">
        <div className="vd-overview-header">
          <div>
            <span className="vd-panel-eyebrow">Admin</span>
            <h3 className="vd-client-growth-title">Users Overview</h3>
            <p className="vd-client-growth-subtitle">
              Every user&apos;s connected accounts and returns.
            </p>
          </div>

          <div className="vd-toggle-group vd-overview-range">
            {OVERVIEW_RANGES.map((r) => (
              <button
                key={r}
                type="button"
                className={`vd-toggle ${dateRange === r ? 'active' : ''}`}
                onClick={() => setDateRange(r)}
              >
                {r}
              </button>
            ))}
          </div>
        </div>

        {error && <p className="vd-error">{error}</p>}

        {emptyState ? (
          <div className="vd-trade-history-empty">{emptyState}</div>
        ) : (
          <div className="vd-table-wrap">
            <table className="vd-table vd-overview-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Account</th>
                  <th className="vd-num">Total Return</th>
                  <th className="vd-num" title="Most recent daily move; not affected by the time-range filter">Today&apos;s Change</th>
                  <th className="vd-num">Drawdown</th>
                  <th>Chart</th>
                </tr>
              </thead>
              <tbody>
                {accounts.map((account) => {
                  const positive = sparklinePositive(account.totalReturn) ?? false;
                  const isActive = activeAccountId === account.id;
                  return (
                    <tr
                      key={account.id}
                      className={`vd-overview-row${isActive ? ' active' : ''}${onSelectAccount ? ' vd-overview-row-clickable' : ''}`}
                      role={onSelectAccount ? 'button' : undefined}
                      tabIndex={onSelectAccount ? 0 : undefined}
                      onClick={onSelectAccount ? () => onSelectAccount(account) : undefined}
                      onKeyDown={onSelectAccount ? (e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectAccount(account);
                        }
                      } : undefined}
                      aria-label={onSelectAccount ? `View ${account.userName} — ${account.accountName}` : undefined}
                    >
                      <td>
                        <span className="vd-overview-account">
                          <UserBadge account={account} />
                          <span className="vd-overview-account-name">{account.userName}</span>
                        </span>
                      </td>
                      <td>
                        <span className="vd-overview-account">
                          <BrokerBadge account={account} />
                          <span className="vd-overview-account-name">{account.accountName}</span>
                        </span>
                      </td>
                      <td className={`vd-num ${positive ? 'vd-positive' : 'vd-negative'}`}>
                        {formatPct(account.totalReturn)}
                      </td>
                      <td className="vd-num">
                        <span className={`vd-overview-change-pill ${account.changePct >= 0 ? 'up' : 'down'}`}>
                          {formatSignedPct(account.changePct)}
                        </span>
                      </td>
                      <td className="vd-num">{formatPct(account.maxDrawdown)}</td>
                      <td>
                        <Sparkline values={account.sparkline} positive={positive} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </section>
  );
}
